import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { register as registerUser } from '../services/api';
import Button from '../components/Button';
import Input from '../components/Input';
import { getLegalFileLocale, t } from '../i18n';
import './AuthPages.css';

const RegisterPage: React.FC = () => {
  const legalLocale = getLegalFileLocale();
  const termsUrl = `https://x-perienc.es/terms-of-service/${legalLocale}.html`;
  const privacyUrl = `https://x-perienc.es/privacy-policy/${legalLocale}.html`;

  const [alias, setAlias] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [acceptedTerms, setAcceptedTerms] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [registeredEmail, setRegisteredEmail] = useState('');
  const [emailDeliveryWarning, setEmailDeliveryWarning] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    if (password !== confirmPassword) {
      setError(t('register.error.passwordsMismatch'));
      return;
    }

    if (password.length < 6) {
      setError(t('register.error.passwordMin'));
      return;
    }

    if (!acceptedTerms) {
      setError(t('register.error.acceptTerms'));
      return;
    }

    setLoading(true);

    try {
      const response = await registerUser({
        alias,
        email,
        password,
        acceptedTerms
      });
      setSuccess(true);
      setRegisteredEmail(response.data.email || email);
      setEmailDeliveryWarning(response.data.emailSent === false);
    } catch (err: any) {
      setError(t('register.error.generic'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="auth-page">
      <div className="auth-container">
        <div className="auth-header">
          <img src="/logo.webp" alt="X-periences" className="auth-logo" />
          <h1 className="auth-title">{success ? t('register.titleSuccess') : t('register.title')}</h1>
          <p className="auth-subtitle">
            {success ? t('register.subtitleSuccess') : t('register.subtitle')}
          </p>
        </div>

        {!success ? (
          <form onSubmit={handleSubmit} className="auth-form">
            {error && <div className="auth-error">{error}</div>}
            
            <Input
              label={t('register.alias')}
              type="text"
              placeholder={t('register.aliasPlaceholder')}
              value={alias}
              onChange={(e) => setAlias(e.target.value)}
              required
            />

            <Input
              label={t('common.email')}
              type="email"
              placeholder={t('common.emailExample')}
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
            />

            <Input
              label={t('common.password')}
              type="password"
              placeholder={t('register.passwordPlaceholder')}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              minLength={6}
            />

            <Input
              label={t('common.confirmPassword')}
              type="password"
              placeholder={t('register.confirmPasswordPlaceholder')}
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              required
            />

            <label className="legal-consent">
              <input
                type="checkbox"
                checked={acceptedTerms}
                onChange={(e) => setAcceptedTerms(e.target.checked)}
                required
              />
              <span>
                {t('register.acceptPrefix')}{' '}
                <a href={termsUrl} target="_blank" rel="noopener noreferrer" className="auth-link">
                  {t('register.terms')}
                </a>{' '}
                {t('register.and')}{' '}
                <a href={privacyUrl} target="_blank" rel="noopener noreferrer" className="auth-link">
                  {t('register.privacy')}
                </a>
                {t('register.acceptSuffix')}
              </span>
            </label>

            <Button type="submit" fullWidth loading={loading}>
              {t('register.submit')}
            </Button>
          </form>
        ) : (
          <div className="auth-form">
            <div className="form-success">
              <p style={{ marginBottom: '15px' }}>
                ✓ {t('register.successCreated')}
              </p>
              <p style={{ fontSize: '14px', color: 'var(--text-muted)', marginBottom: '10px' }}>
                {t('register.successSentTo')}
              </p>
              <p style={{ fontSize: '16px', fontWeight: '600', color: 'var(--text)', marginBottom: '15px' }}>
                {registeredEmail}
              </p>
              <p style={{ fontSize: '14px', color: 'var(--text-muted)' }}>
                {emailDeliveryWarning ? t('register.successEmailIssue') : t('register.successCheckInbox')}
              </p>
            </div>
            <Link to="/login">
              <Button fullWidth variant="secondary">{t('register.successBackToLogin')}</Button>
            </Link>
          </div>
        )}

        <div className="auth-footer">
          {t('register.haveAccount')}{' '}
          <Link to="/login" className="auth-link">{t('register.signIn')}</Link>
        </div>
      </div>
    </div>
  );
};

export default RegisterPage;
